import { useQuery, useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { mediaBase } from './configs';
import type { Presets, CreatePresetGroup, PresetDetail } from './vod-presets-types';

// ============================================
// Query keys
// ============================================

export const vodPresetKeys = {
  all: ['vod', 'presets'] as const,
  groups: (serviceId: string) =>
    ['vod', 'presets', 'groups', serviceId] as const,
  detail: (serviceId: string, presetId: string | number, presetGroupId: string | undefined) =>
    ['vod', 'presets', 'detail', serviceId, presetId, presetGroupId] as const,
};

// ============================================
// Queries
// ============================================

/** List all preset groups available for this service. */
export function useVodPresetGroups(serviceId: string) {
  return useQuery<Presets[]>({
    queryKey: vodPresetKeys.groups(serviceId),
    enabled: !!serviceId,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, 'vod')}/preset-groups`);
      // The envelope is `{ data: Presets[] }`, but when a zone has no groups the
      // backend returns `{ data: null }` — falling back to the raw envelope then
      // yields an object, which downstream `.map()` chokes on. Always resolve to
      // an array.
      const data = res.data?.data ?? res.data;
      return Array.isArray(data) ? data : [];
    },
  });
}

/**
 * Fetch a single preset's codec/bitrate detail. Optionally scoped to a preset
 * group: `presetId/presetGroupId` mirrors the legacy segment construction.
 */
export function usePresetDetail(
  serviceId: string,
  presetId: string | number | null,
  presetGroupId?: string,
) {
  return useQuery<PresetDetail>({
    queryKey: vodPresetKeys.detail(serviceId, presetId ?? '', presetGroupId),
    enabled: !!serviceId && !!presetId,
    queryFn: async () => {
      const segment = presetGroupId ? `${presetId}/${presetGroupId}` : `${presetId}`;
      const res = await serviceApiV3.get(`${mediaBase(serviceId, 'vod')}/preset/${segment}`);
      return res.data?.data ?? res.data;
    },
  });
}

// ============================================
// Mutations
// ============================================

export function useCreatePresetGroup(
  serviceId: string,
  options?: UseMutationOptions<void, Error, CreatePresetGroup>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (presetGroup: CreatePresetGroup) => {
      await serviceApiV3.post(`${mediaBase(serviceId, 'vod')}/preset-groups`, presetGroup);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: vodPresetKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeletePresetGroup(
  serviceId: string,
  options?: UseMutationOptions<void, Error, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (presetGroupId: string) => {
      await serviceApiV3.delete(`${mediaBase(serviceId, 'vod')}/preset-groups/${presetGroupId}`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: vodPresetKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}
